import React from "react"
import { ReactTable } from "@aix/ui"

interface MetaCampaign {
  id: string
  name: string
  objective: string
  status: "ACTIVE" | "PAUSED" | "DELETED" | "ARCHIVED"
  effective_status: string
  configured_status: string
  created_time: string
  updated_time: string
  start_time?: string
  stop_time?: string
  daily_budget?: string
  lifetime_budget?: string
  bid_strategy?: string
  buying_type?: string
}

interface CampaignsTableProps {
  campaigns: MetaCampaign[]
  isLoading: boolean
  onCampaignClick?: (campaign: MetaCampaign) => void
}

export function CampaignsTable({ campaigns, isLoading, onCampaignClick }: CampaignsTableProps) {
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-800"
      case "paused":
        return "bg-yellow-100 text-yellow-800"
      case "deleted":
      case "archived":
        return "bg-gray-100 text-gray-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const formatCurrency = (amount?: string) => {
    if (!amount) return "-"
    const num = parseInt(amount) / 100 // Meta returns cents
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(num)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const columns = React.useMemo(
    () => [
      {
        Header: "Campaign Name",
        accessor: "name" as keyof MetaCampaign,
        Cell: ({ row }: { row: { original: MetaCampaign } }) => (
          <div>
            <div className="font-medium text-foreground">{row.original.name}</div>
            <div className="text-sm text-muted-foreground">ID: {row.original.id}</div>
          </div>
        ),
      },
      {
        Header: "Objective",
        accessor: "objective" as keyof MetaCampaign,
        Cell: ({ value }: { value: string }) => (
          <span className="capitalize">{value.toLowerCase().replace(/_/g, " ")}</span>
        ),
      },
      {
        Header: "Status",
        accessor: "effective_status" as keyof MetaCampaign,
        Cell: ({ value }: { value: string }) => (
          <span className={`inline-block px-2 py-1 text-xs rounded-full ${getStatusColor(value)}`}>{value}</span>
        ),
      },
      {
        Header: "Budget",
        accessor: "daily_budget" as keyof MetaCampaign,
        Cell: ({ row }: { row: { original: MetaCampaign } }) => {
          const { daily_budget, lifetime_budget } = row.original
          if (daily_budget) {
            return (
              <div>
                <div className="font-medium">{formatCurrency(daily_budget)}</div>
                <div className="text-xs text-muted-foreground">Daily</div>
              </div>
            )
          }
          if (lifetime_budget) {
            return (
              <div>
                <div className="font-medium">{formatCurrency(lifetime_budget)}</div>
                <div className="text-xs text-muted-foreground">Lifetime</div>
              </div>
            )
          }
          return "-"
        },
      },
      {
        Header: "Created",
        accessor: "created_time" as keyof MetaCampaign,
        Cell: ({ value }: { value: string }) => (
          <div>
            <div className="text-sm">{formatDate(value)}</div>
          </div>
        ),
      },
      {
        Header: "Dates",
        accessor: "start_time" as keyof MetaCampaign,
        Cell: ({ row }: { row: { original: MetaCampaign } }) => {
          const { start_time, stop_time } = row.original
          return (
            <div className="text-sm">
              {start_time && <div>Start: {formatDate(start_time)}</div>}
              {stop_time && <div>End: {formatDate(stop_time)}</div>}
              {!start_time && !stop_time && "-"}
            </div>
          )
        },
      },
      {
        Header: "Bid Strategy",
        accessor: "bid_strategy" as keyof MetaCampaign,
        Cell: ({ value }: { value: string | undefined }) => (
          <span className="text-sm capitalize">{value ? value.toLowerCase().replace(/_/g, " ") : "-"}</span>
        ),
      },
    ],
    [],
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading campaigns...</p>
        </div>
      </div>
    )
  }

  if (campaigns.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No campaigns found</h3>
        <p className="text-muted-foreground">
          This account doesn&apos;t have any campaigns yet, or they may not be visible with current filters.
        </p>
      </div>
    )
  }

  return (
    <ReactTable
      data={campaigns}
      columns={columns}
      onRowClick={onCampaignClick ? (row: any, e: any) => onCampaignClick(row) : undefined}
      className="min-h-[400px]"
    />
  )
}
